import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import { CheckCircle, AlertCircle, Mail, RefreshCw, Loader2 } from 'lucide-react'
import api from '../lib/api'

/**
 * Email Verification Page
 *
 * Handles the email verification flow when a user clicks the verification
 * link in their email. This page:
 * 1. Extracts the token from the URL
 * 2. Extracts the tenant from the query params
 * 3. Calls the backend to verify the email with the correct tenant
 * 4. Shows success/error state with appropriate actions
 */
export default function EmailVerify() {
  const { token } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const tenant = searchParams.get('tenant')
  const navigate = useNavigate()

  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'expired'>('loading')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [isResending, setIsResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setMessage('No verification token provided.')
      return
    }

    verifyEmail()
  }, [token])

  const verifyEmail = async () => {
    if (!token) return

    setStatus('loading')
    try {
      const result = await api.verifyEmail(token, tenant || undefined)
      setStatus('success')
      setMessage(result.message)

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login')
      }, 3000)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Verification failed'

      if (errorMessage.toLowerCase().includes('expired')) {
        setStatus('expired')
        setMessage('This verification link has expired. Please request a new one.')
      } else {
        setStatus('error')
        setMessage(errorMessage)
      }
    }
  }

  const handleResendVerification = async () => {
    if (!email || isResending || resendCooldown > 0) return

    setIsResending(true)
    setResendSuccess(false)

    try {
      const result = await api.resendVerificationEmail(email, tenant || undefined)
      setResendSuccess(true)

      if (result.already_verified) {
        setMessage('Your email is already verified. You can log in now.')
        setTimeout(() => navigate('/login'), 2000)
      } else {
        // Start cooldown
        setResendCooldown(60)
        const timer = setInterval(() => {
          setResendCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(timer)
              return 0
            }
            return prev - 1
          })
        }, 1000)
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Failed to resend verification email')
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-700 via-primary-600 to-primary-500 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 mb-4">
              <img src="/logos/clarantis-icon.svg" alt="Clarantis" className="w-20 h-20" />
            </div>
            <h1 className="text-2xl font-bold text-primary-700">Clarantis</h1>
            <p className="text-gray-500 mt-1">Email Verification</p>
          </div>

          {/* Loading State */}
          {status === 'loading' && (
            <div className="text-center py-8">
              <Loader2 className="h-12 w-12 text-primary-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Verifying your email address...</p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="space-y-6">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-green-800 font-medium">Email Verified!</p>
                  <p className="text-sm text-green-700 mt-1">{message}</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600">
                  Redirecting to login in a few seconds...
                </p>
              </div>

              <Link
                to="/login"
                className="block w-full btn-primary py-3 text-base text-center"
              >
                Go to Login
              </Link>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="space-y-6">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-800 font-medium">Verification Failed</p>
                  <p className="text-sm text-red-700 mt-1">{message}</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 text-center">
                  The verification link may be invalid or has already been used.
                </p>
              </div>

              <Link
                to="/login"
                className="block w-full btn-primary py-3 text-base text-center"
              >
                Go to Login
              </Link>
            </div>
          )}

          {/* Expired State */}
          {status === 'expired' && (
            <div className="space-y-6">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
                <AlertCircle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800 font-medium">Link Expired</p>
                  <p className="text-sm text-amber-700 mt-1">{message}</p>
                </div>
              </div>

              {/* Resend Form */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-center mb-3">
                  <Mail className="h-8 w-8 text-primary-600" />
                </div>
                <p className="text-sm text-gray-600 text-center mb-4">
                  Enter your email address to receive a new verification link.
                </p>

                <div className="space-y-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input w-full"
                    placeholder="Enter your email"
                  />

                  {resendSuccess && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700 text-center">
                        Verification email sent! Check your inbox.
                      </p>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={!email || isResending || resendCooldown > 0}
                    className="w-full btn-primary py-2.5 inline-flex items-center justify-center"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${isResending ? 'animate-spin' : ''}`} />
                    {resendCooldown > 0
                      ? `Resend in ${resendCooldown}s`
                      : isResending
                        ? 'Sending...'
                        : 'Resend Verification Email'
                    }
                  </button>
                </div>
              </div>

              <Link
                to="/login"
                className="block w-full text-center text-sm text-primary-600 hover:text-primary-700"
              >
                Back to Login
              </Link>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-white/80">
          Governance, Risk & Compliance Platform
        </p>
      </div>
    </div>
  )
}
